import React from 'react';
import {Alert} from '@mui/material';
import { LessonStep } from '../../types';
import StepNavigation from '../StepNavigation';
import AllahNameStep from './AllahNameStep';
import AyahSearchStep from './AyahSearchStep';
import IntroductionStep from './IntroductionStep';
import ListenStep from './ListenStep';
import PracticeStep from './PracticeStep';
import PronunciationStep from './PronunciationStep';
import RecognitionStep from './RecognitionStep';
import SyllablesStep from './SyllablesStep';
import TajweedStep from './TajweedStep';
import TermsStep from './TermsStep';
import WritingStep from './WritingStep';

const supportedSteps=new Set(['introduction','listen','syllables','recognition','pronunciation','writing','practice','ayah_search','tajweed','terms','allah_name']);

interface StepRendererProps {
  step: LessonStep;
  isLastStep?: boolean;
  token: string | null;
  onNext: (score?: number) => void;
  onBack?: () => void;
  onSkip?: () => void;
  onBackToLetters?: () => void;
  lessonId?: string;
  canGoBack?: boolean;
  canSkip?: boolean;
}

const StepRenderer: React.FC<StepRendererProps> = React.memo(({
  step,
  isLastStep = false,
  token,
  onNext,
  onBack,
  onSkip,
  onBackToLetters,
  lessonId,
  canGoBack = true,
  canSkip = true
}) => {
  console.log('🎬 StepRenderer rendering step:', step.step_type, 'order:', step.order);

  // Мемоизируем рендеринг шага на основе типа шага
  const renderedStep = (() => {
    switch (step.step_type) {
      case 'introduction':
        return (
          <IntroductionStep
            step={step}
            isLastStep={isLastStep}
            token={token}
            onNext={onNext}
            onBackToLetters={onBackToLetters}
            lessonId={lessonId}
          />
        );

      case 'listen':
        return (
          <ListenStep
            step={step}
            token={token}
            onNext={onNext}
            onBackToLetters={onBackToLetters}
            lessonId={lessonId}
          />
        );

      case 'syllables':
        return (
          <SyllablesStep
            step={step}
            isLastStep={isLastStep}
            token={token}
            onNext={onNext}
            onBackToLetters={onBackToLetters}
            lessonId={lessonId}
          />
        );

      case 'recognition':
        return (
          <RecognitionStep
            step={step}
            isLastStep={isLastStep}
            token={token}
            onNext={onNext}
            onBackToLetters={onBackToLetters}
            lessonId={lessonId}
          />
        );

      case 'pronunciation':
        return (
          <PronunciationStep
            step={step}
            isLastStep={isLastStep}
            token={token}
            onNext={(score) => onNext(score)}
            onBackToLetters={onBackToLetters}
            lessonId={lessonId}
          />
        );

      case 'writing':
        return (
          <WritingStep
            step={step}
            isLastStep={isLastStep}
            token={token}
            onNext={onNext}
            onBackToLetters={onBackToLetters}
            lessonId={lessonId}
          />
        );

      case 'practice':
        return (
          <PracticeStep
            step={step}
            isLastStep={isLastStep}
            token={token}
            onNext={onNext}
            onBackToLetters={onBackToLetters}
            lessonId={lessonId}
          />
        );

      case 'ayah_search':
        return (
          <AyahSearchStep
            step={step}
            isLastStep={isLastStep}
            token={token}
            onNext={onNext}
            onBackToLetters={onBackToLetters}
            lessonId={lessonId}
          />
        );

      case 'tajweed':
        return (
          <TajweedStep
            step={step}
            token={token}
            onNext={onNext}
            onBackToLetters={onBackToLetters}
            lessonId={lessonId}
          />
        );

      case 'terms':
        return (
          <TermsStep
            step={step}
            token={token}
            onNext={onNext}
            onBackToLetters={onBackToLetters}
            lessonId={lessonId}
          />
        );

      case 'allah_name':
        return (
          <AllahNameStep
            step={step}
            token={token}
            onNext={onNext}
            onBackToLetters={onBackToLetters}
            lessonId={lessonId}
          />
        );

      default:
        return (
          <div>
            <h3>{step.title}</h3>
            <Alert severity="error">Этот тип шага пока недоступен. Результат не сохранён.</Alert>
          </div>
        );
    }
  })();

  return (
    <div>
      {renderedStep}
      <StepNavigation
        onBack={onBack}
        onSkip={onSkip}
        onBackToLetters={onBackToLetters}
        canGoBack={canGoBack}
        canSkip={canSkip && supportedSteps.has(step.step_type)}
        showBackToLetters={true}
      />
    </div>
  );
});

StepRenderer.displayName = 'StepRenderer';

export default StepRenderer;
